/**
 * Session: the signed-in principal from /app/v1/me. Unauthenticated users go to NusaIOS SSO
 * (no second login, ADR 0007). Permissions here are cosmetic hints; the server decides.
 */
import { useQuery } from "@tanstack/react-query";
import { createContext, useContext, useEffect, type ReactNode } from "react";

import { changeLocale, type Locale } from "../i18n";
import { api, ApiError, call, loginUrl, setCsrfToken } from "./api";
import type { components } from "./api-types";

export type Me = components["schemas"]["MeOut"];

const SessionContext = createContext<Me | null>(null);

export function useSession(): Me {
  const me = useContext(SessionContext);
  if (!me) throw new Error("useSession outside SessionProvider");
  return me;
}

export function useMeQuery() {
  return useQuery({
    queryKey: ["me"],
    queryFn: () => call<Me>(api.GET("/app/v1/me")),
    retry: false,
    staleTime: 60_000,
  });
}

export function SessionProvider({ me, children }: { me: Me; children: ReactNode }) {
  useEffect(() => {
    setCsrfToken(me.csrf_token ?? null);
  }, [me.csrf_token]);
  useEffect(() => {
    const url = new URLSearchParams(window.location.search).get("lng");
    if (!url) void changeLocale(me.locale as Locale);
  }, [me.locale]);
  return <SessionContext.Provider value={me}>{children}</SessionContext.Provider>;
}

export function redirectToLogin(): void {
  const here = window.location.pathname + window.location.search;
  window.location.assign(loginUrl(here));
}

export function isUnauthenticated(err: unknown): boolean {
  return err instanceof ApiError && err.status === 401;
}
